import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Toolbar,
  Card,
  CardHeader,
  CardContent,
  Button,
  Paper,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Add, Refresh, Edit } from "@mui/icons-material";

import HeaderBar from "../../components/HeaderBar";
import LeftDrawer from "../../components/LeftDrawer";
import AddLoanTypeModal from "../../components/modals/AddLoanTypeModalNew";
import { TableSkeleton } from "../../components/common/SkeletonLoader";
import { useToast } from "../../components/common/ToastProvider";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EnhancedTable from "../../components/common/EnhancedTable";
import {
  createLoanType,
  fetchLoanTypes,
} from "../../redux/masters/loanTypeSlice";
import { getOptions } from "../../redux/masters/optionSlice";
import { OptionsStatus } from "../../constants/enum";

const LoanTypeList = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    items: loanTypes,
    loading,
    error,
  } = useSelector((state) => state.loanTypes);
  const {
    data: loanOptions = {},
    loading: optionLoading,
    error: optionError,
  } = useSelector((state) => state.option || {});
  const optionKey = OptionsStatus.LoanMaster;
  const optionData = loanOptions[optionKey] || [];

  const { showSuccess, showError } = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editingLoanId, setEditingLoanId] = useState(null); // for edit
  const [addLoading, setAddLoading] = useState(false); // for add

  useEffect(() => {
    dispatch(fetchLoanTypes());
  }, [dispatch]);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDrawerClose = () => setMobileOpen(false);
  const handleCloseModal = () => setOpenModal(false);

  const handleAddNew = async () => {
    try {
      setAddLoading(true);
      await ensureLoanOptionsLoaded();
      setEditData(null);
      setOpenModal(true);
    } catch (err) {
      showError("Failed to load loan options");
    } finally {
      setAddLoading(false);
    }
  };

  const ensureLoanOptionsLoaded = async () => {
    if (!optionData || optionData.length === 0) {
      try {
        await dispatch(getOptions([optionKey])).unwrap();
      } catch (err) {
        showError("Failed to load options");
        throw err; // so the caller can decide what to do
      }
    }
  };

  const handleRefresh = async () => {
    try {
      await dispatch(fetchLoanTypes()).unwrap();
      showSuccess("Data refreshed successfully");
    } catch (error) {
      showError(error.message || "Failed to refresh data");
    }
  };

  const handleSaveLoanType = async (form) => {
    try {
      await dispatch(createLoanType(form)).unwrap();
      await dispatch(fetchLoanTypes()).unwrap();
      showSuccess("Loan type created successfully");
      handleCloseModal();
    } catch (error) {
      showError(error.message || "Failed to create loan type");
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <HeaderBar onMobileMenuToggle={handleDrawerToggle} />
      <LeftDrawer
        activeController="/masters/loan"
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerClose}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: "100vh",
          width: { sm: `calc(100% - ${isMobile ? 0 : 240}px)` },
        }}
      >
        <AddLoanTypeModal
          optionData={optionData}
          optionLoading={optionLoading}
          optionError={optionError}
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setEditData(null);
          }}
          onSave={handleSaveLoanType}
          data={editData} // pass selected item to modal
        />

        <Toolbar sx={{ minHeight: 64 }} />
        <Breadcrumbs />

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardHeader
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: "background.secondary",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            title={
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Loan Type Master
              </Typography>
            }
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleAddNew}
                  startIcon={
                    addLoading ? <CircularProgress size={16} /> : <Add />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                  disabled={addLoading}
                >
                  {isMobile ? "Add" : "Add New"}
                </Button>

                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Refresh
                  </Button>
                )}
              </Box>
            }
          />
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {loading ? (
              <TableSkeleton rows={5} columns={13} />
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : (
              <EnhancedTable
                data={loanTypes || []}
                columns={[
                  {
                    id: "loanTypeCode",
                    label: "Code",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "loanTypeName",
                    label: "Name",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "interestRate",
                    label: "ROI (%)",
                    sortable: true,
                    render: (value) => `${value}%`,
                    hidden: isMobile,
                  },
                  {
                    id: "maxLoanAmount",
                    label: "Max Loan",
                    sortable: true,
                    render: (value) => value ? `₹${value.toLocaleString()}` : "",
                    hidden: isMobile,
                  },
                  {
                    id: "rebate",
                    label: "Rebate(%)",
                    sortable: true,
                    render: (value) => `${value}%`,
                    hidden: isMobile,
                  },
                  {
                    id: "penaltyIntRate",
                    label: "Penal Int.(%)",
                    sortable: true,
                    render: (value) => `${value}%`,
                    hidden: isMobile,
                  },
                  {
                    id: "active",
                    label: "Status",
                    sortable: true,
                    render: (value) =>
                      value ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip label="Inactive" size="small" variant="outlined" />
                      ),
                  },
                  {
                    id: "editable",
                    label: "Editable",
                    sortable: false,
                    render: (_, row) => (
                      <Tooltip
                        title={
                          row.editable
                            ? "Edit Loan Type"
                            : "Data is not editable"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              !row.editable || editingLoanId !== null
                            }
                            onClick={async (e) => {
                              e.stopPropagation();
                              setEditingLoanId(row.id);
                              try {
                                await ensureLoanOptionsLoaded();
                                setEditData(row);
                                setOpenModal(true);
                              } catch (err) {
                                // handle error
                              } finally {
                                setEditingLoanId(null);
                              }
                            }}
                          >
                            {editingLoanId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Edit fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                ]}
                title="Loan Types"
                onRowClick={(row) => console.log("Row clicked:", row)}
                loading={loading}
                emptyMessage="No loan types found"
                stickyHeader={true}
                dense={true}
                showRowNumbers={true}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LoanTypeList;
